const BOOKING_DURATION_TYPES = {
  HOURLY: 'hourly',
  HALF_DAY: 'halfDay',
  FULL_DAY: 'fullDay',
};

const isPlainObject = value => {
  return !!value && typeof value === 'object' && !Array.isArray(value);
};

const definedEntries = object => {
  return Object.entries(object || {}).reduce(
    (result, [key, value]) => (typeof value === 'undefined' ? result : { ...result, [key]: value }),
    {}
  );
};

const firstDefined = (...values) =>
  values.find(value => typeof value !== 'undefined' && value !== null && value !== '');

const validDate = value => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const nestedValue = isPlainObject(value) && value.date ? value.date : value;
  const numericValue =
    typeof nestedValue === 'string' && /^\d+$/.test(nestedValue)
      ? Number(nestedValue)
      : nestedValue;
  const date =
    typeof numericValue === 'number' || typeof numericValue === 'string'
      ? new Date(numericValue)
      : null;

  return date && !Number.isNaN(date.getTime()) ? date : null;
};

const parseBookingSlot = bookingSlot => {
  if (isPlainObject(bookingSlot)) {
    return bookingSlot;
  }

  if (typeof bookingSlot !== 'string' || !bookingSlot.trim()) {
    return null;
  }

  try {
    const parsed = JSON.parse(bookingSlot);
    return isPlainObject(parsed) ? parsed : null;
  } catch (e) {
    return null;
  }
};

const normalizeDurationType = durationType => {
  if (typeof durationType !== 'string') {
    return null;
  }

  const normalized = durationType
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, '');

  if (normalized === 'hour' || normalized === 'hourly') {
    return BOOKING_DURATION_TYPES.HOURLY;
  }
  if (normalized === 'halfday') {
    return BOOKING_DURATION_TYPES.HALF_DAY;
  }
  if (normalized === 'fullday' || normalized === 'day') {
    return BOOKING_DURATION_TYPES.FULL_DAY;
  }

  return durationType;
};

const getConfiguredDurationTypes = publicData => {
  const bookingTypes = Array.isArray(publicData?.bookingType)
    ? publicData.bookingType
    : typeof publicData?.bookingType === 'string'
    ? [publicData.bookingType]
    : [];
  const enabledRates = publicData?.advancePricingDetails?.enabledRates || {};
  const enabledDurationTypes = Object.entries(enabledRates)
    .filter(([, enabled]) => enabled)
    .map(([durationType]) => durationType);

  return [...new Set([...bookingTypes, ...enabledDurationTypes].map(normalizeDurationType))].filter(
    durationType => Object.values(BOOKING_DURATION_TYPES).includes(durationType)
  );
};

const getPricingMatrix = (advancePricingDetails, bookingType) => {
  const pricingMatrices = advancePricingDetails?.pricingMatrices || {};
  const availableBookingTypes = Object.keys(pricingMatrices);
  const normalizedBookingType =
    bookingType && pricingMatrices[bookingType]
      ? bookingType
      : availableBookingTypes.length === 1
      ? availableBookingTypes[0]
      : advancePricingDetails?.pricingModel === 'model2' && pricingMatrices.MULTIPURPOSE
      ? 'MULTIPURPOSE'
      : bookingType;

  return {
    bookingType: normalizedBookingType,
    matrix: pricingMatrices[normalizedBookingType] || [],
  };
};

const isValidRate = rate => {
  if (rate === null || typeof rate === 'undefined' || rate === '' || rate === '-') {
    return false;
  }

  const numericRate = Number(rate);
  return Number.isFinite(numericRate) && numericRate >= 0;
};

const findConfiguredRate = ({ advancePricingDetails, bookingType, crewSize, durationType }) => {
  if (!bookingType || !crewSize || !durationType) {
    return undefined;
  }

  const matrix = advancePricingDetails?.pricingMatrices?.[bookingType] || [];
  const matchingRow = matrix.find(row => row?.crewLabel === crewSize);
  const configuredRate = matchingRow?.[durationType];

  return isValidRate(configuredRate) ? configuredRate : undefined;
};

const getTimeInTimeZone = (date, timeZone) => {
  if (!date || !timeZone || typeof Intl === 'undefined') {
    return null;
  }

  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(date);
    const hour = parts.find(part => part.type === 'hour')?.value;
    const minute = parts.find(part => part.type === 'minute')?.value;
    return hour && minute ? `${hour}:${minute}` : null;
  } catch (e) {
    return null;
  }
};

const normalizeHalfDayPeriod = ({
  halfDayPeriod,
  bookingStart,
  bookingEnd,
  halfDayTimes,
  timeZone,
}) => {
  const normalizedPeriod =
    typeof halfDayPeriod === 'string' ? halfDayPeriod.trim().toLowerCase() : null;

  if (['morning', 'am'].includes(normalizedPeriod)) {
    return 'morning';
  }
  if (['afternoon', 'pm'].includes(normalizedPeriod)) {
    return 'afternoon';
  }

  const startTime = getTimeInTimeZone(bookingStart, timeZone);
  const endTime = getTimeInTimeZone(bookingEnd, timeZone);

  if (
    startTime === halfDayTimes?.morningStart &&
    (!halfDayTimes?.morningEnd || endTime === halfDayTimes.morningEnd)
  ) {
    return 'morning';
  }
  if (
    startTime === halfDayTimes?.afternoonStart &&
    (!halfDayTimes?.afternoonEnd || endTime === halfDayTimes.afternoonEnd)
  ) {
    return 'afternoon';
  }

  return null;
};

const getDurationInHours = (bookingStart, bookingEnd) => {
  if (!bookingStart || !bookingEnd || bookingEnd <= bookingStart) {
    return null;
  }

  return (bookingEnd.getTime() - bookingStart.getTime()) / (60 * 60 * 1000);
};

/**
 * Normalize booking information written by ListingPage, TransactionPage inquiry handoffs,
 * session storage restores, and transaction protected data.
 *
 * The returned shape keeps the checkout-facing bookingDates object while also exposing flat
 * bookingStart/bookingEnd values for server-side line-item calculation.
 */
const normalizeBookingOrderData = ({ listing, orderData = {}, protectedData = null } = {}) => {
  const rawOrderData = isPlainObject(orderData) ? orderData : {};
  const rawProtectedData = isPlainObject(protectedData)
    ? protectedData
    : isPlainObject(rawOrderData.protectedData)
    ? rawOrderData.protectedData
    : {};
  const publicData = listing?.attributes?.publicData || {};
  const advancePricingDetails = publicData.advancePricingDetails || {};
  const protectedBookingModalDetails = isPlainObject(rawProtectedData.bookingModalDetails)
    ? rawProtectedData.bookingModalDetails
    : {};
  const orderBookingModalDetails = isPlainObject(rawOrderData.bookingModalDetails)
    ? rawOrderData.bookingModalDetails
    : {};
  const legacyBookingModalDetails = definedEntries({
    bookingType: rawOrderData.bookingType,
    crewSize: rawOrderData.crewSize,
    durationType: rawOrderData.durationType,
    selectedTimeSlots: rawOrderData.selectedTimeSlots,
    rate: rawOrderData.rate,
    overTime: rawOrderData.overTime,
    isOverTime: rawOrderData.isOverTime,
    bookingSlot: rawOrderData.bookingSlot,
    halfDayPeriod: rawOrderData.halfDayPeriod,
  });
  const bookingModalDetails = {
    ...protectedBookingModalDetails,
    ...legacyBookingModalDetails,
    ...orderBookingModalDetails,
  };
  // Advanced hourly listings use duration variants (hourly / half-day / full-day).
  // Day and night listings may expose similarly named public data, but their quantity
  // must continue to come from the selected date range.
  const configuredDurationTypes =
    publicData.unitType === 'hour' ? getConfiguredDurationTypes(publicData) : [];
  const durationType =
    normalizeDurationType(
      firstDefined(
        bookingModalDetails.durationType,
        rawOrderData.durationType,
        rawProtectedData.durationType
      )
    ) || (configuredDurationTypes.length === 1 ? configuredDurationTypes[0] : null);
  const pricingData = getPricingMatrix(
    advancePricingDetails,
    firstDefined(
      bookingModalDetails.bookingType,
      rawOrderData.bookingType,
      rawProtectedData.bookingType
    )
  );
  const bookingType = pricingData.bookingType;
  const projectInformation =
    rawProtectedData.projectInformation || rawOrderData.projectInformation || {};
  const crewSize = firstDefined(
    bookingModalDetails.crewSize,
    rawOrderData.crewSize,
    rawProtectedData.crewSize,
    projectInformation?.crewSize
  );
  const bookingSlot = firstDefined(
    bookingModalDetails.bookingSlot,
    rawOrderData.bookingSlot,
    rawProtectedData.bookingSlot
  );
  const parsedBookingSlot = parseBookingSlot(bookingSlot);
  const rawBookingDates = isPlainObject(rawOrderData.bookingDates) ? rawOrderData.bookingDates : {};
  const bookingStart =
    validDate(
      firstDefined(
        rawBookingDates.bookingStart,
        rawBookingDates.startDate,
        rawOrderData.bookingStart,
        rawProtectedData.bookingStart,
        parsedBookingSlot?.start?.timestamp,
        parsedBookingSlot?.start
      )
    ) || null;
  const bookingEnd =
    validDate(
      firstDefined(
        rawBookingDates.bookingEnd,
        rawBookingDates.endDate,
        rawOrderData.bookingEnd,
        rawProtectedData.bookingEnd,
        parsedBookingSlot?.end?.timestamp,
        parsedBookingSlot?.end
      )
    ) || null;
  const configuredRate = findConfiguredRate({
    advancePricingDetails,
    bookingType,
    crewSize,
    durationType,
  });
  const submittedRate = firstDefined(
    bookingModalDetails.rate,
    rawOrderData.rate,
    rawProtectedData.rate
  );
  const submittedRateMatchesConfiguration =
    isValidRate(submittedRate) &&
    isValidRate(configuredRate) &&
    Number(submittedRate) === Number(configuredRate);
  const rate = isValidRate(configuredRate)
    ? submittedRateMatchesConfiguration
      ? submittedRate
      : configuredRate
    : isValidRate(submittedRate)
    ? submittedRate
    : undefined;
  const rawSelectedTimeSlots = firstDefined(
    bookingModalDetails.selectedTimeSlots,
    rawOrderData.selectedTimeSlots,
    rawProtectedData.selectedTimeSlots
  );
  const selectedTimeSlots = isPlainObject(rawSelectedTimeSlots) ? { ...rawSelectedTimeSlots } : {};
  const halfDayTimes =
    selectedTimeSlots.halfDayTimes ||
    bookingModalDetails.halfDayTimes ||
    advancePricingDetails.halfDayTimes;
  const halfDayPeriod =
    durationType === BOOKING_DURATION_TYPES.HALF_DAY
      ? normalizeHalfDayPeriod({
          halfDayPeriod: firstDefined(
            bookingModalDetails.halfDayPeriod,
            selectedTimeSlots.halfDayPeriod,
            rawOrderData.halfDayPeriod,
            rawProtectedData.halfDayPeriod
          ),
          bookingStart,
          bookingEnd,
          halfDayTimes,
          timeZone:
            listing?.attributes?.availabilityPlan?.timezone ||
            publicData?.availabilityPlan?.timezone,
        })
      : null;
  const normalizedSelectedTimeSlots =
    durationType === BOOKING_DURATION_TYPES.HALF_DAY
      ? {
          ...selectedTimeSlots,
          ...(halfDayTimes ? { halfDayTimes } : {}),
          ...(halfDayPeriod ? { halfDayPeriod } : {}),
          ...(parsedBookingSlot ? { selectedSlot: parsedBookingSlot } : {}),
        }
      : durationType === BOOKING_DURATION_TYPES.FULL_DAY && advancePricingDetails.fullDayTimes
      ? {
          ...selectedTimeSlots,
          fullDayTimes: selectedTimeSlots.fullDayTimes || advancePricingDetails.fullDayTimes,
          ...(parsedBookingSlot ? { selectedSlot: parsedBookingSlot } : {}),
        }
      : selectedTimeSlots;
  const bookingDurationInHours = getDurationInHours(bookingStart, bookingEnd);
  const numberOfUnits =
    durationType === BOOKING_DURATION_TYPES.HALF_DAY ||
    durationType === BOOKING_DURATION_TYPES.FULL_DAY
      ? 1
      : bookingDurationInHours;
  const normalizedBookingModalDetails = {
    ...bookingModalDetails,
    ...definedEntries({
      bookingType,
      crewSize,
      durationType,
      selectedTimeSlots: normalizedSelectedTimeSlots,
      rate,
      bookingSlot,
      halfDayPeriod,
      bookingDurationInHours,
      numberOfUnits,
    }),
  };
  const hasBookingDetails = !!durationType || Object.keys(bookingModalDetails).length > 0;
  const normalizedProtectedData = hasBookingDetails
    ? {
        ...rawProtectedData,
        bookingModalDetails: normalizedBookingModalDetails,
        ...definedEntries({
          durationType,
          halfDayPeriod,
          bookingDurationInHours,
          numberOfUnits,
        }),
      }
    : rawProtectedData;

  return {
    ...rawOrderData,
    ...(bookingStart || bookingEnd
      ? {
          bookingDates: {
            ...rawBookingDates,
            ...(bookingStart ? { bookingStart } : {}),
            ...(bookingEnd ? { bookingEnd } : {}),
          },
        }
      : {}),
    ...(bookingStart ? { bookingStart } : {}),
    ...(bookingEnd ? { bookingEnd } : {}),
    ...(hasBookingDetails ? { bookingModalDetails: normalizedBookingModalDetails } : {}),
    ...(Object.keys(normalizedProtectedData).length > 0
      ? { protectedData: normalizedProtectedData }
      : {}),
    ...(hasBookingDetails
      ? definedEntries({
          bookingType,
          crewSize,
          durationType,
          selectedTimeSlots: normalizedSelectedTimeSlots,
          rate,
          bookingSlot,
          halfDayPeriod,
          bookingDurationInHours,
          numberOfUnits,
        })
      : {}),
  };
};

const getBookingDebugData = orderData => {
  const bookingModalDetails =
    orderData?.protectedData?.bookingModalDetails || orderData?.bookingModalDetails || {};
  const bookingDates = orderData?.bookingDates || {};
  const bookingStart = bookingDates.bookingStart || orderData?.bookingStart;
  const bookingEnd = bookingDates.bookingEnd || orderData?.bookingEnd;
  const debugDate = value =>
    value instanceof Date
      ? Number.isNaN(value.getTime())
        ? 'invalid-date'
        : value.toISOString()
      : value || null;

  return {
    bookingStart: debugDate(bookingStart),
    bookingEnd: debugDate(bookingEnd),
    durationType: firstDefined(bookingModalDetails.durationType, orderData?.durationType) ?? null,
    halfDayPeriod: firstDefined(bookingModalDetails.halfDayPeriod, orderData?.halfDayPeriod) ?? null,
    bookingDurationInHours:
      firstDefined(
        bookingModalDetails.bookingDurationInHours,
        orderData?.bookingDurationInHours
      ) ?? null,
    numberOfUnits:
      firstDefined(bookingModalDetails.numberOfUnits, orderData?.numberOfUnits) ?? null,
    bookingType: firstDefined(bookingModalDetails.bookingType, orderData?.bookingType) ?? null,
    crewSize: firstDefined(bookingModalDetails.crewSize, orderData?.crewSize) ?? null,
    rate: firstDefined(bookingModalDetails.rate, orderData?.rate) ?? null,
  };
};

module.exports = {
  BOOKING_DURATION_TYPES,
  getBookingDebugData,
  normalizeBookingOrderData,
  normalizeDurationType,
  parseBookingSlot,
};
